/**
 * PATH: packages/ui/src/kanban/components/kanbanTouchChrome.ts
 * FILE_ID: UI-KANBAN-TOUCH-CHROME-001
 * PURPOSE: Tracks which single card currently has its quick-action rail held
 *   open by touch, and owns every rule that closes it again.
 * WHY:
 *   On a mouse the rail needs no bookkeeping: it follows the pointer, and
 *   leaving the card closes it. A finger has no "leave", so a touch-opened rail
 *   would stay open forever, and long-pressing a second card would leave two
 *   rails open at once. Both dismissal rules ("tap outside closes it", "opening
 *   another card closes the previous one") are inherently CROSS-CARD, and a
 *   card component cannot see its siblings — so the state lives here, in one
 *   module-level slot, rather than being duplicated per card.
 *   Modelled deliberately on ./kanbanHoverIntent.ts, which solves the sibling
 *   problem for scroll the same way; keeping the two idioms identical means a
 *   reader who knows one already knows the other.
 * FLOW:
 *   long-press ─▶ openKanbanTouchChrome(id) ─┬─▶ previous card closes (one slot)
 *                                            └─▶ subscribers re-render
 *   outside pointerdown / board scroll / Escape ─▶ close ─▶ subscribers
 * DEPENDENCIES:
 *   - ./kanbanHoverIntent: reuses the board's existing scroll-activity signal
 *     so a rail opened by hold is dismissed by the same scroll that suppresses
 *     hover chrome — one definition of "the operator is scrolling".
 * RELATED:
 *   - ./useKanbanCardInteractions.ts: opens this on long-press, reads the flag.
 *   - ./KanbanCardQuickActionsPopover.tsx: its portal carries the
 *     data-attribute below so tapping INSIDE a picker is not "outside".
 */
import { useEffect, useState } from 'react'

import { subscribeKanbanScrollActive } from './kanbanHoverIntent'

/** Card id whose touch rail is open, or null. Exactly one, by construction. */
let activeCardId: string | number | null = null

type Listener = (activeCardId: string | number | null) => void
const listeners = new Set<Listener>()

/**
 * Teardown for the document listeners + scroll subscription, held only while a
 * rail is actually open. Idle boards pay nothing, and there is no listener to
 * leak once everything closes.
 */
let teardown: (() => void) | null = null

function publish(next: string | number | null): void {
  if (activeCardId === next) return
  activeCardId = next
  for (const listener of listeners) listener(activeCardId)
}

/**
 * f(target) -> is this node part of the open card's own UI?
 *
 * "Inside" is wider than the card element: the rail's pickers are PORTALED to
 * document.body, so by DOM containment a tap on the assignee list would look
 * like a tap on the board and close the rail out from under the operator.
 */
function isInsideOpenChrome(target: Node | null): boolean {
  if (!(target instanceof Element)) return false
  if (activeCardId != null && target.closest(`[data-card-id="${activeCardId}"]`)) {
    return true
  }
  // Any portaled surface the rail itself spawned.
  return !!target.closest('[data-kanban-card-quick-popover]')
}

function ensureListeners(): void {
  if (teardown || typeof document === 'undefined') return

  const onPointerDown = (e: Event) => {
    if (!isInsideOpenChrome(e.target as Node)) closeKanbanTouchChrome()
  }
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') closeKanbanTouchChrome()
  }
  // Scrolling the board is the operator moving on, exactly as it is for hover
  // chrome — reuse that signal rather than inventing a second scroll notion.
  const unsubscribeScroll = subscribeKanbanScrollActive((scrolling) => {
    if (scrolling) closeKanbanTouchChrome()
  })

  // Capture phase: a picker button that calls stopPropagation must not be able
  // to stop the board from noticing a tap landed outside some OTHER card.
  document.addEventListener('pointerdown', onPointerDown, true)
  document.addEventListener('keydown', onKeyDown, true)

  teardown = () => {
    teardown = null
    document.removeEventListener('pointerdown', onPointerDown, true)
    document.removeEventListener('keydown', onKeyDown, true)
    unsubscribeScroll()
  }
}

/** f(cardId) -> open this card's touch rail, closing any other card's. */
export function openKanbanTouchChrome(cardId: string | number): void {
  ensureListeners()
  publish(cardId)
}

/**
 * f(cardId?) -> close the touch rail.
 * Passing an id makes it a no-op unless that card is the one currently open, so
 * a card unmounting cannot close a rail that has already moved on to a sibling.
 */
export function closeKanbanTouchChrome(cardId?: string | number): void {
  if (cardId != null && activeCardId !== cardId) return
  publish(null)
  teardown?.()
}

/** f(cardId) -> open this card, or close it if it is already the open one. */
export function toggleKanbanTouchChrome(cardId: string | number): void {
  if (activeCardId === cardId) {
    closeKanbanTouchChrome(cardId)
    return
  }
  openKanbanTouchChrome(cardId)
}

/** f() -> the card id whose touch rail is open, or null. */
export function getKanbanTouchChromeCardId(): string | number | null {
  return activeCardId
}

/** f(listener) -> unsubscribe. Fires immediately with the current value. */
export function subscribeKanbanTouchChrome(listener: Listener): () => void {
  listeners.add(listener)
  listener(activeCardId)
  return () => {
    listeners.delete(listener)
  }
}

/** React hook: is THIS card's quick-action rail currently held open by touch? */
export function useKanbanTouchChromeOpen(cardId: string | number): boolean {
  const [open, setOpen] = useState(() => activeCardId === cardId)
  useEffect(
    () => subscribeKanbanTouchChrome((active) => setOpen(active === cardId)),
    [cardId],
  )
  return open
}

/** Test-only: drop listeners + force closed. */
export function __resetKanbanTouchChromeForTests(): void {
  teardown?.()
  publish(null)
}
